"use client";

import Image from "next/image";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { useState, useCallback } from "react";
import { GoogleReCaptchaCheckbox } from "@google-recaptcha/react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { contactSchema, type ContactFormValues } from "@/lib/contact.schema";
import { toast } from "sonner";
import GooeyButton from "./GooeyButton";

export default function TellUs() {
    const [date, setDate] = useState<Date>();
    const [recaptchaToken, setRecaptchaToken] = useState("");
    const [recaptchaKey, setRecaptchaKey] = useState(0);
    const [errorMessage, setErrorMessage] = useState("");
    const isRecaptchaConfigured = Boolean(process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY);

    const handleRecaptchaChange = useCallback((token: string) => {
        setRecaptchaToken(token);
        setErrorMessage("");
    }, []);

    const handleRecaptchaReset = useCallback(() => {
        setRecaptchaToken("");
        setRecaptchaKey((prev) => prev + 1);
    }, []);

    const {
        register,
        handleSubmit,
        setValue,
        reset,
        formState: { errors, isSubmitting },
    } = useForm<ContactFormValues>({
        resolver: zodResolver(contactSchema),
        defaultValues: {
            fullName: "",
            email: "",
            phone: "",
            eventType: "",
            eventDate: "",
            venueLocation: "",
            numberOfGuests: "",
            musicPreferences: "",
            additionalNotes: "",
        },
    });

    const onSubmit = async (data: ContactFormValues) => {
        if (!isRecaptchaConfigured) {
            setErrorMessage("reCAPTCHA is not configured. Please add NEXT_PUBLIC_RECAPTCHA_SITE_KEY to .env.");
            return;
        }
        if (!recaptchaToken) {
            setErrorMessage("Please complete the reCAPTCHA");
            return;
        }

        try {
            const res = await fetch("/api/contact", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    formId: process.env.NEXT_PUBLIC_CONTACT_FORM_ID || "",
                    payload: data,
                    recaptchaToken
                }),
            });

            const result = await res.json();

            if (!res.ok || !result.success) {
                throw new Error(result?.error || "Submission failed");
            }

            if (result?.success === true) {
                toast.success("Your booking request has been sent successfully!");
            }

            // Reset form
            reset();
            setDate(undefined);
            handleRecaptchaReset();
        } catch (error) {
            console.error(error);
            toast.error("Something went wrong. Please try again.");
        }
    };

    return (
        <div className="w-full px-[5%] sm:mt-[-2vw] py-16 sm:pb-[2vw] flex flex-col items-center justify-center gap-12 relative z-10" id="form">

            {/* Glow */}
            <div className="absolute right-[-15vw] bottom-[-10vw] -z-10 h-[300px] w-[300px] sm:h-[25vw] sm:w-[25vw] rounded-full bg-[#400077]/50 blur-[130px]"></div>

            <div className="absolute left-[-15vw] bottom-[-10vw] -z-10 h-[300px] w-[300px] sm:h-[25vw] sm:w-[25vw] rounded-full bg-[#0073FF]/20 blur-[130px]"></div>

            <div className="w-full max-w-6xl flex flex-col items-center">
                <h2 className="text-3xl sm:text-[3vw] font-bold bg-linear-to-r from-[#B8BBBF] to-[#0073FF] bg-clip-text text-transparent mb-12 text-center">
                    Tell Us About Your Event
                </h2>

                <div className="flex flex-col sm:flex-row items-stretch justify-center w-full relative">

                    {/* Left: Image (Man pointing) */}
                    <div className="hidden sm:flex w-[25vw] items-end justify-center z-20 absolute top-[-14.5vw] left-[-1.5vw] 2xl:left-[-3.5vw] pointer-events-none">
                        <Image
                            src="/contact1.png"
                            alt="DJ Sachin"
                            width={1080}
                            height={2000}
                            className="w-full h-auto object-contain object-bottom"
                        />
                    </div>

                    {/* Right: Form Card */}
                    <div className="p-px rounded-2xl bg-linear-[300deg] from-[#00417F] via-[#007AFF] to-[#3CFFF4]">
                        <div className="w-full bg-[#130724] rounded-2xl p-6 sm:p-10 backdrop-blur-md shadow-[0_0_50px_rgba(216,40,148,0.1)] flex flex-col gap-6 z-10">
                            <p className="text-[#E2E8F0] text-sm sm:text-[1.1vw] sm:text-base">
                                Share Your Event Details and Receive a Customized Entertainment Consultation.
                            </p>

                            <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5">
                                {/* Row 1 */}
                                <div className="flex flex-col sm:flex-row gap-4">
                                    <div className="flex-1 flex flex-col gap-1">
                                        <Input
                                            placeholder="Full Name"
                                            className="bg-[#0a0410]/80 border-[#3B1563] text-[#e2e2e2] placeholder:text-[#6b7280] h-12"
                                            {...register("fullName")}
                                            onInput={(e) => {
                                                e.currentTarget.value = e.currentTarget.value.replace(/[^a-zA-Z\s]/g, '');
                                            }}
                                        />
                                        {errors.fullName && <p className="text-xs text-red-400">{errors.fullName.message}</p>}
                                    </div>
                                    <div className="flex-1 flex flex-col gap-1">
                                        <Input
                                            placeholder="Email"
                                            type="email"
                                            className="bg-[#0a0410]/80 border-[#3B1563] text-[#e2e2e2] placeholder:text-[#6b7280] h-12"
                                            {...register("email")}
                                        />
                                        {errors.email && <p className="text-xs text-red-400">{errors.email.message}</p>}
                                    </div>
                                </div>

                                {/* Row 2 */}
                                <div className="flex flex-col sm:flex-row gap-4">
                                    <div className="flex-1 flex flex-col gap-1">
                                        <Input
                                            placeholder="Phone Number"
                                            type="tel"
                                            inputMode="tel"
                                            className="bg-[#0a0410]/80 border-[#3B1563] text-[#e2e2e2] placeholder:text-[#6b7280] h-12"
                                            {...register("phone")}
                                            onInput={(e) => {
                                                e.currentTarget.value = e.currentTarget.value.replace(/[^0-9+]/g, '');
                                            }}
                                        />
                                        {errors.phone && <p className="text-xs text-red-400">{errors.phone.message}</p>}
                                    </div>

                                    <div className="flex-1 flex flex-col gap-1">
                                        <Select
                                            modal={false}
                                            onValueChange={(val: string | null) => {
                                                if (val) setValue("eventType", val, { shouldValidate: true });
                                            }}
                                        >
                                            <SelectTrigger className="w-full bg-[#0a0410]/80 border-[#3B1563] text-[#e2e2e2] !h-12 data-[placeholder]:text-[#6b7280]">
                                                <SelectValue placeholder="Select Event Type" />
                                            </SelectTrigger>
                                            <SelectContent className="bg-[#130724] border-[#3B1563] text-[#e2e2e2]" style={{ fontFamily: 'Lato' }}>
                                                <SelectItem value="wedding">Wedding</SelectItem>
                                                <SelectItem value="corporate">Corporate</SelectItem>
                                                <SelectItem value="party">Private Party</SelectItem>
                                                <SelectItem value="other">Other</SelectItem>
                                            </SelectContent>
                                        </Select>
                                        {errors.eventType && <p className="text-xs text-red-400">{errors.eventType.message}</p>}
                                    </div>

                                    <div className="flex-1 flex flex-col gap-1">
                                        <Popover>
                                            <PopoverTrigger
                                                className={cn(
                                                    "flex w-full items-center justify-between rounded-lg border border-[#3B1563] bg-[#0a0410]/80 px-3 py-2 text-sm text-[#e2e2e2] h-12 ring-offset-background placeholder:text-[#6b7280] outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50 hover:bg-[#130724]",
                                                    !date && "text-[#6b7280]"
                                                )}
                                                style={{ fontFamily: 'Lato' }}
                                            >
                                                {date ? format(date, "PPP") : <span>Event Date</span>}
                                                <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                                            </PopoverTrigger>
                                            <PopoverContent className="w-auto p-0 border-[#3B1563] bg-[#130724] text-[#e2e2e2]" align="start">
                                                <Calendar
                                                    mode="single"
                                                    selected={date}
                                                    onSelect={(d) => {
                                                        setDate(d);
                                                        if (d) {
                                                            setValue("eventDate", format(d, "yyyy-MM-dd"), { shouldValidate: true });
                                                        }
                                                    }}
                                                    disabled={{ before: new Date() }}
                                                    className="bg-[#130724] text-[#e2e2e2] border-0"
                                                    style={{ fontFamily: 'Lato' }}
                                                />
                                            </PopoverContent>
                                        </Popover>
                                        {errors.eventDate && <p className="text-xs text-red-400">{errors.eventDate.message}</p>}
                                    </div>
                                </div>

                                {/* Row 3 */}
                                <div className="flex flex-col sm:flex-row gap-4">
                                    <div className="flex-1 flex flex-col gap-1">
                                        <Input
                                            placeholder="Venue Location"
                                            className="bg-[#0a0410]/80 border-[#3B1563] text-[#e2e2e2] placeholder:text-[#6b7280] h-12"
                                            {...register("venueLocation")}
                                        />
                                    </div>
                                    <div className="flex-1 flex flex-col gap-1">
                                        <Input
                                            placeholder="Number of Guests"
                                            type="text"
                                            inputMode="numeric"
                                            pattern="[0-9]*"
                                            className="bg-[#0a0410]/80 border-[#3B1563] text-[#e2e2e2] placeholder:text-[#6b7280] h-12"
                                            {...register("numberOfGuests")}
                                            onInput={(e) => {
                                                e.currentTarget.value = e.currentTarget.value.replace(/[^0-9]/g, '');
                                            }}
                                        />
                                    </div>
                                </div>

                                {/* Row 4 (Textareas) */}
                                <div className="flex flex-col sm:flex-row gap-4">
                                    <div className="flex-1 flex flex-col gap-2">
                                        <label className="text-xs text-[#B8BBBF] ml-1">Music Preferences</label>
                                        <Textarea
                                            placeholder="e.g. Live Band, DJ, Pop, Jazz, etc."
                                            className="min-h-[120px] bg-[#0a0410]/80 border-[#3B1563] text-[#e2e2e2] placeholder:text-[#6b7280] resize-none"
                                            {...register("musicPreferences")}
                                        />
                                    </div>
                                    <div className="flex-1 flex flex-col gap-2">
                                        <label className="text-xs text-[#B8BBBF] ml-1">Additional Notes</label>
                                        <Textarea
                                            placeholder="Provide any special requests..."
                                            className="min-h-[120px] bg-[#0a0410]/80 border-[#3B1563] text-[#e2e2e2] placeholder:text-[#6b7280] resize-none"
                                            {...register("additionalNotes")}
                                        />
                                    </div>
                                </div>

                                {/* Error Message */}
                                {errorMessage && (
                                    <p className="text-sm text-red-400 mt-2">{errorMessage}</p>
                                )}

                                {/* reCAPTCHA Checkbox */}
                                <div className="space-y-2 my-4 flex justify-center">
                                    {isRecaptchaConfigured ? (
                                        <GoogleReCaptchaCheckbox
                                            key={`recaptcha-${recaptchaKey}`}
                                            onChange={handleRecaptchaChange}
                                            className="transform scale-75"
                                        />
                                    ) : (
                                        <p className="text-sm text-yellow-300 text-center">
                                            reCAPTCHA is not configured.
                                        </p>
                                    )}
                                </div>

                                {/* Submit Button */}
                                <div className="flex justify-center absolute -bottom-9 sm:bottom-[-21px] left-1/2 translate-x-[-50%]">
                                    <div className="bg-linear-to-t from-[#CA00F7] to-[#0073FF] rounded-full p-[2px] hover:bg-none transition-all focus:outline-none focus:ring-2 focus:ring-[#74EAF8] focus:ring-offset-2 focus:ring-offset-[#0a0410]">
                                        <GooeyButton
                                            type="submit"
                                            disabled={isSubmitting}
                                            className="rounded-full w-full h-full block"
                                        >
                                            <div className="px-6 sm:px-8 py-2.5">
                                                <span className="text-white font-semibold text-base transition-all duration-300 group-hover:text-black">
                                                    {isSubmitting ? "Submitting..." : "Request Booking"}
                                                </span>
                                            </div>
                                        </GooeyButton>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}